import styled, { css } from 'styled-components';
import type { ColorName } from 'styles/config/colors';
import themeColors from 'styles/config/colors';
import sizes from 'styles/config/sizes';
import type { SizeKey } from 'styles/config/spacing';
import themeSpacing from 'styles/config/spacing';
import rem from 'styles/functions/rem';

import { Title } from 'components/atoms/LegacyText';

export const Tab = styled.button<{
  $secondary?: boolean;
  $fullWidth?: boolean;
  $selected?: boolean;
  $withBorderBottom?: boolean;
}>`
  cursor: pointer;

  position: relative;

  overflow: hidden;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  min-width: ${rem(90)};
  max-width: ${sizes.modalWidthSmall};
  min-height: ${themeSpacing.sm1};
  margin: 0;
  padding: ${themeSpacing.xs1} ${themeSpacing.sm1};

  color: ${themeColors.textSecondary};

  background-color: transparent;
  border: 0;
  border-radius: 0;
  outline: 0;

  transition: all 0.2s ease-in-out;

  ${({ $secondary }) =>
    $secondary &&
    css`
      min-width: unset;
    `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      flex: 1 1 0px;
      max-width: none;
    `}

  ${({ $withBorderBottom }) =>
    $withBorderBottom &&
    css`
      border-bottom: 2px solid transparent;
    `}

  ${({ $selected, $withBorderBottom }) =>
    $selected &&
    css`
      cursor: default;
      color: inherit;
      ${
        $withBorderBottom &&
        css`
        border-bottom-color: ${themeColors.borderPrimary};
      `
      }
    `}

  &:hover {
    color: inherit;
  }

  &:disabled {
    cursor: pointer;
    color: ${themeColors.textDisabled};
  }
`;

export const Indicator = styled.div<{
  $color?: ColorName | 'transparent';
  $left?: number;
  $width?: number;
}>`
  position: relative;
  left: ${({ $left }) => $left}px;

  width: ${({ $width }) => $width}px;
  height: 2px;

  background-color: ${({ $color, $left, $width }) =>
    (!$left && !$width) || $color === 'transparent'
      ? 'transparent'
      : $color
        ? themeColors[$color]
        : themeColors.backgroundBrand};

  transition: all 0.33s cubic-bezier(0.38, 0.8, 0.32, 1.07);
`;

export const TabTitle = styled(Title)`
  display: flex;
  align-items: center;
`;

export const TabIcon = styled.span<{
  $spacing?: SizeKey;
  $inline?: boolean;
  $iconPosition?: 'start' | 'end';
}>`
  display: ${({ $inline }) => ($inline ? 'inline-block' : 'flex')};
  flex-shrink: 0;
  align-items: center;
  ${({ $iconPosition, $spacing }) => {
    const spacingValue = $spacing ? themeSpacing[$spacing] : themeSpacing.xs1;
    if ($iconPosition === 'start') {
      return `margin-right: ${spacingValue}; margin-left: 0;`;
    }
    return `margin-left: ${spacingValue}; margin-right: 0;`;
  }}
`;
